import React, { Dispatch, SetStateAction } from "react";
import { Dropdown } from "react-bootstrap";

import { StateLanguage } from "../../languages/config/StateLanguage";
import { legendValid } from "../../utils/legend.data.util";
import { inventoryTypeList, inventoryTypeValue } from "../../libraries/inventory.type.library";
import { suffixLegend } from "../../scripts/name.legend.script";

import AppLegend from "./Legend";

export interface AppInputUnitTypeProps {
    options: { index: number; item: { id: string; type: string; name: string; symbol: string } } | null;
    typeUnit?: { value: string; valid: boolean };
    measureUnits?: {
        value: { id: string; type: string; name: string; symbol: string }[];
        valid: { type: boolean; name: boolean; symbol: boolean }[];
    };
    setTypeUnit?: Dispatch<SetStateAction<{ value: string; valid: boolean }>>;
    setMeasureUnits?: Dispatch<
        SetStateAction<{
            value: { id: string; type: string; name: string; symbol: string }[];
            valid: { type: boolean; name: boolean; symbol: boolean }[];
        }>
    >;
}

const AppInputUnitType: React.FunctionComponent<AppInputUnitTypeProps> = ({
    options,
    typeUnit,
    measureUnits,
    setTypeUnit,
    setMeasureUnits,
}) => {
    const { lang } = StateLanguage();

    // Modo simple: usado desde modales con un único valor
    const handleChangeType = (item: string) => {
        if (typeUnit && setTypeUnit) {
            setTypeUnit({ value: item, valid: true });
            legendValid("legend-unit-type-required");
        }
    };

    // Modo lista: usado desde tablas/formularios con múltiples unidades
    const handleChangeUnitTypeList = (index: number, item: string) => {
        if (measureUnits && setMeasureUnits) {
            const tempUnitsValue = measureUnits.value.map((temp_item, temp_index) => {
                if (index === temp_index) {
                    temp_item.type = item;
                }
                return temp_item;
            });
            const tempUnitsValid = measureUnits.valid.map((temp_item, temp_index) => {
                if (index === temp_index) {
                    temp_item.type = true;
                }
                return temp_item;
            });

            setMeasureUnits({ value: tempUnitsValue, valid: tempUnitsValid });
            legendValid(`legend-unit-type-required${suffixLegend(index)}`);
        }
    };

    return (
        <>
            <Dropdown>
                <Dropdown.Toggle variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-100 p-0">
                    <span className="selection">
                        <span className="select2-selection select2-selection--single form-select">
                            <span className="select2-selection__rendered" role="textbox">
                                {typeUnit && <>{typeUnit.valid ? inventoryTypeValue(lang, typeUnit.value) : lang.labels.selectOption}</>}
                                {options && measureUnits && (
                                    <>
                                        {measureUnits.valid[options.index].type
                                            ? inventoryTypeValue(lang, options.item.type)
                                            : lang.labels.selectOption}
                                    </>
                                )}
                            </span>
                        </span>
                    </span>
                </Dropdown.Toggle>
                <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100">
                    <span className="select2-dropdown select2-dropdown--below">
                        <span className="select2-results">
                            <ul className="select2-results__options" role="listbox">
                                {typeUnit &&
                                    inventoryTypeList(lang).map((item, index) => (
                                        <li
                                            key={index}
                                            className={`select2-results__option select2-results__option--selectable ${
                                                typeUnit.value === item.code && "select2-results__option--selected"
                                            }`}
                                            role="option"
                                            aria-selected={typeUnit.value === item.code}
                                        >
                                            <Dropdown.Item
                                                bsPrefix="select2-results__option__text"
                                                onClick={() => handleChangeType(item.code)}
                                            >
                                                {item.display}
                                            </Dropdown.Item>
                                        </li>
                                    ))}
                                {options &&
                                    measureUnits &&
                                    inventoryTypeList(lang).map((sub_item, sub_index) => (
                                        <li
                                            key={sub_index}
                                            className={`select2-results__option select2-results__option--selectable ${
                                                options.item.type === sub_item.code && "select2-results__option--selected"
                                            }`}
                                            role="option"
                                            aria-selected={options.item.type === sub_item.code}
                                        >
                                            <Dropdown.Item
                                                bsPrefix="select2-results__option__text"
                                                onClick={() => handleChangeUnitTypeList(options.index, sub_item.code)}
                                            >
                                                {sub_item.display}
                                            </Dropdown.Item>
                                        </li>
                                    ))}
                            </ul>
                        </span>
                    </span>
                </Dropdown.Menu>
            </Dropdown>

            {typeUnit && (
                <AppLegend
                    attribute={{ check: typeUnit.valid, name: "unit-type", index: null, sub_index: null }}
                    container={{ valid: false, required: true, identical: false, exists: false }}
                />
            )}
            {options && measureUnits && (
                <AppLegend
                    attribute={{ check: measureUnits.valid[options.index].type, name: "unit-type", index: options.index, sub_index: null }}
                    container={{ valid: false, required: true, identical: false, exists: false }}
                />
            )}
        </>
    );
};

export default AppInputUnitType;
